import {
  SURVEY_MD,
  SURVEY_INCLINATION,
  SURVEY_AZIMUTH,
  SURVEY_TVD,
  SURVEY_DLS
} from "./types";
import type { SurveyPointAtMD, MaxDogLegSeverity } from "./types";
import { findClosestRowsByMD } from "./extrapolation";
import { getTrueVerticalDepthFor3DFt } from "./geometry";

function lerp(from: number, to: number, ratio: number): number {
  return from + (to - from) * ratio;
}

function lerpAzimuthDeg(fromDeg: number, toDeg: number, ratio: number): number {
  const delta = ((((toDeg - fromDeg) % 360) + 540) % 360) - 180;
  const result = (fromDeg + delta * ratio) % 360;
  return result < 0 ? result + 360 : result;
}

/**
 * Resolves the survey state at an arbitrary measured depth.
 *
 * Inclination, azimuth and DLS are interpolated between the surrounding survey
 * stations. TVD uses minimum curvature against the upper station so it remains
 * consistent with the survey table.
 */
export function interpolateSurveyAtMD(
  measuredDepthFt: number,
  surveyRows: number[][]
): SurveyPointAtMD | null {
  if (!surveyRows.length) return null;

  const exact = surveyRows.find(row => row[SURVEY_MD] === measuredDepthFt);
  if (exact) {
    return {
      measuredDepthFt,
      inclinationDeg: exact[SURVEY_INCLINATION] ?? 0,
      azimuthDeg: exact[SURVEY_AZIMUTH] ?? 0,
      trueVerticalDepthFt: exact[SURVEY_TVD] ?? 0,
      dogLegSeverityDeg100Ft: exact[SURVEY_DLS] ?? 0
    };
  }

  const [prevRow, nextRow] = findClosestRowsByMD(measuredDepthFt, surveyRows);
  const prevMD = prevRow[SURVEY_MD];
  const nextMD = nextRow[SURVEY_MD];
  const span = nextMD - prevMD;

  if (span <= 0) {
    const row = measuredDepthFt <= prevMD ? prevRow : nextRow;
    return {
      measuredDepthFt,
      inclinationDeg: row[SURVEY_INCLINATION] ?? 0,
      azimuthDeg: row[SURVEY_AZIMUTH] ?? 0,
      trueVerticalDepthFt: row[SURVEY_TVD] ?? 0,
      dogLegSeverityDeg100Ft: row[SURVEY_DLS] ?? 0
    };
  }

  const ratio = (measuredDepthFt - prevMD) / span;
  const inclinationDeg = lerp(
    prevRow[SURVEY_INCLINATION] ?? 0,
    nextRow[SURVEY_INCLINATION] ?? 0,
    ratio
  );
  const azimuthDeg = lerpAzimuthDeg(
    prevRow[SURVEY_AZIMUTH] ?? 0,
    nextRow[SURVEY_AZIMUTH] ?? 0,
    ratio
  );

  return {
    measuredDepthFt,
    inclinationDeg,
    azimuthDeg,
    trueVerticalDepthFt: getTrueVerticalDepthFor3DFt(
      measuredDepthFt,
      prevMD,
      inclinationDeg,
      prevRow[SURVEY_INCLINATION] ?? 0,
      prevRow[SURVEY_TVD] ?? 0,
      azimuthDeg,
      prevRow[SURVEY_AZIMUTH] ?? 0
    ),
    dogLegSeverityDeg100Ft: lerp(
      prevRow[SURVEY_DLS] ?? 0,
      nextRow[SURVEY_DLS] ?? 0,
      ratio
    )
  };
}

export function resampleSurveyAtInterval(
  surveyRows: number[][],
  intervalFt: number,
  endMeasuredDepthFt?: number
): SurveyPointAtMD[] {
  if (!surveyRows.length || intervalFt <= 0) return [];

  const startMD = surveyRows[0][SURVEY_MD];
  const lastMD = surveyRows[surveyRows.length - 1][SURVEY_MD];
  const endMD = endMeasuredDepthFt ?? lastMD;
  if (endMD < startMD) return [];

  const steps = Math.floor((endMD - startMD) / intervalFt);
  const points: SurveyPointAtMD[] = [];

  for (let i = 0; i <= steps; i++) {
    const point = interpolateSurveyAtMD(startMD + i * intervalFt, surveyRows);
    if (point) points.push(point);
  }

  const lastPoint = points[points.length - 1];
  if (!lastPoint || lastPoint.measuredDepthFt < endMD) {
    const point = interpolateSurveyAtMD(endMD, surveyRows);
    if (point) points.push(point);
  }

  return points;
}

/**
 * `Final_Traverse!S5` - the survey station where the traverse puts the string.
 *
 * The sheet scores each station by inclination x DLS (`BN = BP*BS`) and takes
 * the first station with the highest score (`VLOOKUP(S5,BN4:BO464,2,)`). Only
 * stations above the landing depth count (`BS = IF(AX<S3,BB)`). The name is
 * from the earlier rule, which used the DLS alone.
 */
export function findMaxDlsAlongTraverse(
  surveyRows: number[][],
  maxMeasuredDepthFt?: number
): MaxDogLegSeverity | null {
  if (!surveyRows.length) return null;

  let best: MaxDogLegSeverity | null = null;
  let bestScore = 0;

  for (const row of surveyRows) {
    const measuredDepthFt = row[SURVEY_MD];
    if (
      maxMeasuredDepthFt !== undefined &&
      measuredDepthFt >= maxMeasuredDepthFt
    ) {
      break;
    }
    const dogLegSeverityDeg100Ft = row[SURVEY_DLS] ?? 0;
    const score = (row[SURVEY_INCLINATION] ?? 0) * dogLegSeverityDeg100Ft;
    if (!best || score > bestScore) {
      best = { measuredDepthFt, dogLegSeverityDeg100Ft };
      bestScore = score;
    }
  }

  return best;
}
